/**
 * useResumeAutosave.js — single authoritative auto-save for the QA Resume Builder
 *
 * Architecture decisions:
 *
 *  ONE KEY:   'qa_resume_builder_v3'
 *    - Previous keys ('resume_builder_draft', 'qa_resume_draft') are
 *      automatically evicted on first load so stale data never surfaces.
 *
 *  SCHEMA VERSION (SCHEMA_VER = 3):
 *    - Stored alongside the data as { _v, savedAt, data }.
 *    - Any draft whose _v !== SCHEMA_VER is silently discarded.
 *    - Bump SCHEMA_VER whenever the formData shape changes in a breaking way
 *      (e.g. renaming a key, changing achievements from string[] to object[]).
 *
 *  CORRUPTION GUARD:
 *    - Every localStorage read is wrapped in try/catch.
 *    - JSON.parse failure → returns null (builder starts fresh).
 *    - Missing required shape (no personal object) → returns null.
 *
 *  MOUNT SKIP:
 *    - The first render is skipped so restoring a draft never immediately
 *      overwrites itself with the same data.
 *
 *  500ms DEBOUNCE:
 *    - Writes are batched — one write per 500ms of inactivity.
 *    - The timer is cleared on unmount to prevent writes after the component
 *      is destroyed (no memory leak, no stale-closure write).
 *
 *  NEVER STORES TO SUPABASE:
 *    - 100% client-side. No network calls. Free-tier safe.
 */

import { useState, useEffect, useRef } from 'react';

// ── Constants ─────────────────────────────────────────────────────────────────

export const STORAGE_KEY = 'qa_resume_builder_v3';
const SCHEMA_VER         = 3;
const DEBOUNCE_MS        = 500;

/** Legacy keys written by previous implementations — evict on first load */
const LEGACY_KEYS = ['resume_builder_draft', 'qa_resume_draft'];

// ── Evict legacy keys (runs once at module load, not per render) ──────────────

try {
  LEGACY_KEYS.forEach(k => localStorage.removeItem(k));
} catch {
  // Private browsing or quota — ignore
}

// ── Internal write ────────────────────────────────────────────────────────────

function writeDraft(formData) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ _v: SCHEMA_VER, savedAt: Date.now(), data: formData })
    );
  } catch {
    // Quota exceeded or private browsing — fail silently, never crash the builder
  }
}

// ── Public: read ──────────────────────────────────────────────────────────────

/**
 * loadDraft() → formData | null
 *
 * Safe to call during useState initialisation:
 *   const [formData, setFormData] = useState(() => loadDraft() ?? defaultForm);
 *
 * Returns null when:
 *   - No draft exists
 *   - JSON is corrupted
 *   - Schema version mismatch (stale draft from old code)
 *   - Draft is missing the required `personal` shape
 */
export function loadDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const envelope = JSON.parse(raw);

    // Schema version guard — discard stale drafts silently
    if (envelope?._v !== SCHEMA_VER) {
      console.warn('[Autosave] Discarding draft with old schema version:', envelope?._v, '(expected:', SCHEMA_VER, ')');
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      return null;
    }

    // Minimum shape check — must have a personal object
    if (!envelope?.data?.personal) {
      console.warn('[Autosave] Discarding draft with invalid shape (missing personal)');
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      return null;
    }

    // Validate critical arrays exist
    const data = envelope.data;
    const requiredArrays = ['experience', 'projects', 'certifications', 'education'];
    for (const key of requiredArrays) {
      if (!Array.isArray(data[key])) {
        console.warn(`[Autosave] Discarding draft with invalid ${key} (not an array)`);
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        return null;
      }
    }

    console.log('[Autosave] Loaded valid draft from localStorage');
    return envelope.data;
  } catch (error) {
    // Corrupted JSON — discard and start fresh
    console.error('[Autosave] Failed to load draft:', error.message);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    return null;
  }
}

// ── Public: clear ─────────────────────────────────────────────────────────────

/**
 * clearDraft()
 *
 * Call after a successful PDF download so the next session starts fresh
 * instead of restoring a completed resume.
 */
export function clearDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore — private browsing or storage unavailable
  }
}

// ── Public: check ─────────────────────────────────────────────────────────────

/**
 * hasDraft() → boolean
 *
 * Returns true if a valid, non-empty draft exists.
 * Use this to show a "Resume draft restored" toast on mount.
 */
export function hasDraft() {
  return loadDraft() !== null;
}

// Cloud sync has been disabled to keep the builder 100% client-side
// and conserve database/network limits.

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * useResumeAutosave(formData) → { lastSavedAt: Date | null }
 *
 * Debounces localStorage writes whenever formData changes.
 * Skips the initial mount to avoid overwriting a just-restored draft.
 * Returns lastSavedAt so the UI can render a subtle "Saved HH:MM" indicator.
 *
 * Usage:
 *   const { lastSavedAt } = useResumeAutosave(formData);
 */
export function useResumeAutosave(formData) {
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const timerRef         = useRef(null);
  const cloudTimerRef    = useRef(null);
  const isMountRef       = useRef(true);
  const latestDataRef    = useRef(formData);
  const clientUpdatedRef = useRef(Date.now());

  // Keep latest data reference up-to-date for visibility/unload handlers
  useEffect(() => {
    latestDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    // Skip the very first render — don't overwrite a just-restored draft
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }

    clientUpdatedRef.current = Date.now();

    // Local save: remain highly responsive (500ms) for quick local recovery
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      writeDraft(formData);
      setLastSavedAt(new Date());
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

  return { lastSavedAt };
}
